import {
  ArrowLeftRight,
  Banknote,
  BriefcaseBusiness,
  ChartColumnBig,
  CreditCard,
  Megaphone,
  ScanLine,
  Settings,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, type ReactElement } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { SECTION_TITLE_KEYS, type NavEntry, type SectionId } from "@/app/auth/navigation.registry";
import {
  groupSectionEntries,
  resolveActiveNavTarget,
  resolveFooterEntries,
  resolveLandingPath,
  resolveSections,
} from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useStepUpLockoutWatcher } from "@/modules/auth/hooks/useStepUpLockoutWatcher";
import {
  useNotificationSoundLoop,
  useNotificationsSocket,
} from "@/modules/dashboard/notifications/hooks/useNotifications";
import { usePayoutRescueBadge } from "@/modules/dashboard/payout-rescue/hooks/usePayoutRescueBadge";
import { VerificationWelcomeGate } from "@/modules/verification/components/VerificationWelcomeGate";
import { ColocaIcon } from "@/shared/components/ColocaIcon";
import { ColocaAppShell } from "@/shared/components/layout/ColocaAppShell";
import { ColocaAppShellNotificationsBell } from "@/shared/components/layout/ColocaAppShellNotificationsBell";
import { ColocaAppSidebar } from "@/shared/components/layout/ColocaAppSidebar";
import type { ColocaNavMainSection } from "@/shared/components/layout/ColocaNavMain";
import type { ColocaNavSecondaryItem } from "@/shared/components/layout/ColocaNavSecondary";
import { DashboardAlertsIntro } from "@/shared/dashboard-alerts/DashboardAlertsIntro";
import { useDashboardAlerts } from "@/shared/dashboard-alerts/useDashboardAlerts";
import useTranslations from "@/shared/lang/useTranslations";
import { usePlatformActivity } from "@/shared/platform-activity/usePlatformActivity";
import { PwaInstallBanner } from "@/shared/pwa/PwaInstallBanner";
import { usePwaInstall } from "@/shared/pwa/usePwaInstall";
import { usePwaUpdate } from "@/shared/pwa/usePwaUpdate";
import { colocaToast } from "@/shared/utils/toast";

const ICON_SIZE = 18;

/** How long one "two-factor required" burst stays announced. Long enough to swallow a screen's parallel 403s. */
const TWO_FACTOR_NOTICE_COOLDOWN_MS = 3000;

const SECTION_ICONS: Record<SectionId, ReactElement> = {
  home: <Banknote size={ICON_SIZE} />,
  operations: <ArrowLeftRight size={ICON_SIZE} />,
  analytics: <ChartColumnBig size={ICON_SIZE} />,
  management: <BriefcaseBusiness size={ICON_SIZE} />,
  cards: <CreditCard size={ICON_SIZE} />,
};

/** Per-entry icons. Only a few items carry one today; the rest render label-only. */
const ENTRY_ICONS: Record<string, ReactElement> = {
  "/app/wallets": <Wallet size={ICON_SIZE} />,
  "/app/breb-qr-pay": <ScanLine size={ICON_SIZE} />,
  "/app/settings": <Settings size={ICON_SIZE} />,
  "/app/news": <Megaphone size={ICON_SIZE} />,
};

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { user, isAuthenticated, isInternalUser, doLogout } = useAuth();
  const navContext = useNavContext();
  const isLandingPage = !!navContext && !search && pathname === resolveLandingPath(navContext);
  useNotificationsSocket(!!isAuthenticated);
  // The PIN's hard lock closes every session server-side; this signs the person out with the reason.
  useStepUpLockoutWatcher();
  // The corner alerts (payins, payouts, batches, cards), each behind its switch in Settings.
  useDashboardAlerts();
  // Which page is in use and for how long, for the staff platform metrics. Fire and forget.
  usePlatformActivity(isAuthenticated ? user?.id : null);
  useNotificationSoundLoop(isInternalUser);
  // Stuck-payout count for the "Rescate de dispersiones" badge — fetched once on mount, INTERNAL only
  // (org members never see the item and must not trigger the call).
  const payoutRescueCount = usePayoutRescueBadge(!!isInternalUser);
  const { t } = useTranslations("global");
  const { canInstall, install } = usePwaInstall();
  const pwaUpdate = usePwaUpdate();

  /**
   * `HttpApiService` dispatches this per 403 `TWO_FACTOR_REQUIRED` response, and a screen that opens four
   * requests at once gets four events. Unguarded that meant four toasts and four history entries for one fact
   * — and react-router's `push` falls back to `window.location.assign` once the browser rate-limits
   * `pushState`, which the user would experience as the app reloading itself. Announce and redirect once per
   * burst instead; the enrolment screen is the same screen either way.
   */
  useEffect(() => {
    const target = "/app/settings?section=seguridad";
    let announced = false;

    const handler = () => {
      if (announced) return;
      announced = true;
      // A later 403 is a new fact worth announcing again; the same burst is not.
      window.setTimeout(() => {
        announced = false;
      }, TWO_FACTOR_NOTICE_COOLDOWN_MS);

      colocaToast.info(t("two_factor_required_generic"));
      if (`${window.location.pathname}${window.location.search}` !== target) {
        navigate(target);
      }
    };

    window.addEventListener("auth:two-factor-required", handler);
    return () => window.removeEventListener("auth:two-factor-required", handler);
  }, [navigate, t]);

  /**
   * The sidebar is now a projection of NAVIGATION_REGISTRY, not a hand-built tree run through a chain of
   * seven filters keyed on `user.role` and `user.scopes`. The router reads the same registry, so a menu item
   * and its route can no longer disagree — which is what let `/app/audit` stay hidden here and remain
   * reachable by URL. Adding a module means one entry in that file, nothing here.
   */
  const sections = useMemo<ColocaNavMainSection[]>(() => {
    if (!navContext) return [];

    const toNavItem = (entry: NavEntry) => ({
      to: entry.to,
      label: entry.labelKey ? t(entry.labelKey) : entry.to,
      badge:
        entry.to === "/app/payout-rescue" && payoutRescueCount != null && payoutRescueCount > 0 ? (
          <span className="flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground tabular-nums">
            {payoutRescueCount}
          </span>
        ) : undefined,
    });

    return resolveSections(navContext).map((section) => ({
      id: section.id,
      title: t(SECTION_TITLE_KEYS[section.id]),
      icon: SECTION_ICONS[section.id],
      // Folding runs INSIDE this memo on purpose. It is pure and depends only on what is already here, so
      // the tree stays identity-stable across navigation and `activeTo` remains a scalar prop — the
      // property that keeps a route change from rebuilding all 42 item objects.
      items: groupSectionEntries(section.entries).map((node) =>
        node.kind === "entry"
          ? toNavItem(node.entry)
          : {
              // A group row is not a destination; its `to` is only a React key and an identity. It is
              // namespaced so it can never collide with a real entry's path.
              to: `group:${node.group.id}`,
              label: t(node.group.labelKey),
              children: node.entries.map(toNavItem),
            },
      ),
    }));
  }, [navContext, t, payoutRescueCount]);

  const footerItems = useMemo<ColocaNavSecondaryItem[]>(() => {
    if (!navContext) return [];
    return resolveFooterEntries(navContext).map((entry) => ({
      to: entry.to,
      label: entry.labelKey ? t(entry.labelKey) : entry.to,
      icon: ENTRY_ICONS[entry.to],
    }));
  }, [navContext, t]);

  /**
   * Which sidebar row is active, asked ONCE for the whole registry.
   *
   * It is a scalar on purpose: `sections` stays identity-stable across navigation (its deps carry no
   * pathname), so moving between routes re-renders the rows without rebuilding all 42 item objects.
   */
  const activeTo = resolveActiveNavTarget(pathname);

  const navUserLabels = useMemo(
    () => ({
      account: t("nav_user_account"),
      settings: t("nav_user_settings"),
      logout: t("nav_user_logout"),
      organization: t("nav_user_organization"),
    }),
    [t],
  );

  const notificationsLabels = useMemo(
    () => ({
      title: t("header_notifications_title"),
      emptyTitle: t("header_notifications_empty"),
      loadMore: t("header_notifications_load_more"),
    }),
    [t],
  );

  const sidebar = (
    <ColocaAppSidebar
      sections={sections}
      footerItems={footerItems}
      activeTo={activeTo}
      brand={{
        label: (
          <>
            Coloca <span className="font-normal!">payments</span>
          </>
        ),
        to: "/app/wallets",
        icon: <ColocaIcon />,
      }}
      user={{
        name: user?.full_name ?? "",
        email: user?.email ?? "",
        avatar: user?.profile_image,
      }}
      organization={user?.organization ? { name: user.organization.name } : undefined}
      navUserLabels={navUserLabels}
      onAccount={() => navigate("/app/profile")}
      onSettings={() => navigate("/app/settings")}
      onLogout={doLogout}
      installApp={
        canInstall ? { label: t("nav_user_install_app"), onInstall: () => void install() } : undefined
      }
      updateApp={
        pwaUpdate.state === "none" ? undefined : { label: t("pwa_update_menu"), onUpdate: pwaUpdate.apply }
      }
    />
  );

  return (
    <>
      <ColocaAppShell
        sidebar={sidebar}
        headerActions={<ColocaAppShellNotificationsBell labels={notificationsLabels} />}
      >
        <Outlet />
      </ColocaAppShell>
      <PwaInstallBanner enabled={isLandingPage} />
      {/* Once, after the organization's KYC/KYB is approved: where the member can go now. */}
      <VerificationWelcomeGate />
      {/* Once per browser, with the first corner alert: what they are, how they sound, where to change them. */}
      <DashboardAlertsIntro />
    </>
  );
};

export default DashboardLayout;
